/**
 * Normalizacija notification payload-a iz API-ja (стабилан id за React кључеве).
 */

export function getNotificationStableId(notification, fallbackIndex = 0) {
  const rawId = notification?.id ?? notification?.notification_id ?? notification?.notificationId;
  if (rawId != null && rawId !== '') return String(rawId);
  return `notification-row-${fallbackIndex}`;
}

function pickRelatedEntityId(raw) {
  const id =
    raw?.related_entity_id ??
    raw?.relatedEntityId ??
    raw?.entity_id ??
    raw?.entityId ??
    null;
  return id != null && id !== '' ? id : null;
}

function pickNotificationType(raw) {
  return (
    raw?.notification_type ??
    raw?.type ??
    raw?.notificationType ??
    raw?.event_type ??
    raw?.eventType ??
    ''
  );
}

/** Текст из API preview-а када registry нема тип. */
export function getApiNotificationBody(notification) {
  return String(
    notification?.text ??
      notification?.body ??
      notification?.message ??
      notification?.description ??
      notification?.content ??
      ''
  ).trim();
}

export function getApiNotificationTitle(notification) {
  return String(notification?.title ?? notification?.subject ?? '').trim();
}

/**
 * Уједначава један запис (укључујући угнежђени `notification` објекат).
 */
export function normalizeNotificationRecord(raw, fallbackIndex = 0) {
  const nested =
    raw?.notification && typeof raw.notification === 'object' ? raw.notification : null;
  const merged = nested ? { ...nested, ...raw } : { ...(raw || {}) };
  const type = pickNotificationType(merged);
  const relatedEntityId = pickRelatedEntityId(merged);

  return {
    ...merged,
    id: getNotificationStableId(merged, fallbackIndex),
    notification_type: type || merged.notification_type,
    type: type || merged.type,
    related_entity_id: relatedEntityId ?? merged.related_entity_id,
  };
}

export function normalizeUnreadNotificationsPayload(data) {
  const arr = Array.isArray(data) ? data : [];
  return arr.map((item, idx) => normalizeNotificationRecord(item, idx));
}

/** Спој preview (dashboard) са пуним записима из /notification/search. */
export function mergeNotificationsWithFullSearch(previewItems, fullItems) {
  const fullById = new Map();
  (fullItems || []).forEach((item, idx) => {
    const normalized = normalizeNotificationRecord(item, idx);
    fullById.set(String(normalized.id), normalized);
  });

  return (previewItems || []).map((preview, idx) => {
    const base = normalizeNotificationRecord(preview, idx);
    const full = fullById.get(String(base.id));
    if (!full) return base;
    return normalizeNotificationRecord({ ...base, ...full }, idx);
  });
}

export function notificationNeedsFullSearchEnrichment(items) {
  return (items || []).some((n) => !pickNotificationType(n));
}

/** Извлачи низ сирових обавештења из dashboard overview или notification search одговора. */
export function extractUnreadNotificationsFromOverview(root) {
  const un = root?.unread_notifications;
  if (un && typeof un === 'object' && !Array.isArray(un)) {
    const rawItems = Array.isArray(un.items) ? un.items : [];
    const totalRaw = Number(un.total);
    const items = normalizeUnreadNotificationsPayload(rawItems);
    const total = Number.isFinite(totalRaw) ? totalRaw : items.length;
    return { items, total };
  }

  const raw =
    root?.notifications ??
    root?.notification_items ??
    (Array.isArray(un) ? un : null) ??
    root?.notification_previews;
  if (!Array.isArray(raw)) return { items: [], total: 0 };
  const items = normalizeUnreadNotificationsPayload(raw);
  return { items, total: items.length };
}

export function getNotificationSentTimestamp(notification) {
  const sentDate =
    notification?.created_at ||
    notification?.created ||
    notification?.sent_at ||
    notification?.createdAt ||
    notification?.sentAt;
  if (!sentDate) return 0;
  const timestamp = new Date(sentDate).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}
